import React, { useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Box, Typography, CircularProgress, TextField } from "@mui/material";
import { apiVariable } from "../../../../api/apiVariable";
import useAuth from "../../../../hooks/useAuth";
import { getHarshEventsTableColumns } from "../../../../data/FormColumns/Fleet/harshEventsTableColumns";
import PersistedDataGrid from "../../../PersistedDataGrid/PersistedDataGrid";
import { GridRowModel } from "@mui/x-data-grid";

const fetchHarshEvents = async (token: string | undefined | null) => {
  const { data } = await axios.get(`${apiVariable}/harshEvents`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

const HarshEventsTable: React.FC = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["harshEvents"],
    queryFn: () => fetchHarshEvents(token),
    enabled: !!token,
  });

  const filteredEvents = useMemo(() => {
    if (!data) return [];
    return data.filter((event: any) => {
      if (!searchTerm) return true;
      const fields = [
        event.harshEventType,
        event.driverName,
        event.truckUnitNumber,
        event.status,
        event.addedBy,
      ].map((f) => f?.toString().toLowerCase() || "");
      return fields.some((f) => f.includes(searchTerm.toLowerCase()));
    });
  }, [data, searchTerm]);

  const columns = getHarshEventsTableColumns();

  const updateMutation = useMutation({
    mutationFn: async (row: any) => {
      const status = row.status;
      const normalizedStatus =
        status === "No Answer" || status === "NoAnswer"
          ? "NoAnswer"
          : status === "Pending"
            ? "Pending"
            : status === "Completed"
              ? "Completed"
              : status;
      const payload = { ...row, status: normalizedStatus };
      const { data } = await axios.put(
        `${apiVariable}/harshEvent/${row.id}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["harshEvents"] });
    },
  });

  const processRowUpdate = async (
    newRow: GridRowModel,
    oldRow: GridRowModel,
  ) => {
    try {
      const updated = { ...oldRow, ...newRow };
      await updateMutation.mutateAsync(updated);
      return updated;
    } catch (e) {
      return oldRow;
    }
  };

  return (
    <Box className="incidents-table-container" height={900}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-end",
        }}
      >
        <TextField
          label="Search"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by Type, Driver, Truck, Status, or Added By"
          sx={{ maxWidth: "100%", width: 400 }}
        />
      </Box>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error">Error: {(error as any).message}</Typography>
      ) : (
        <PersistedDataGrid
          tableName="harshEvents"
          rows={filteredEvents}
          columns={columns}
          processRowUpdate={processRowUpdate}
          sx={{ height: 700 }}
        />
      )}
    </Box>
  );
};

export default HarshEventsTable;
